import { ProfileRoles, RequestStatus, RequestType, UserPermissions } from '@src/utils/enums';
import {
  Answers,
  AssistanceOption,
  FormValues,
  HelpCategoryCode,
  HelpCategoryOption,
  Recommendation as RequestAssistanceRecommendation,
  RegulatorAssistanceParams,
} from './types';

export const supportEmailAddress = 'rrs@usace.army.mil';
export const supportEmailHours = 'Support agents are available 9:00 AM - 9:00 PM EST Monday - Friday.';
export const supportEmailHref = `mailto:${supportEmailAddress}?subject=${encodeURIComponent('RRS Support Request')}`;

const isNoneHelpCategory = (helpCategory: HelpCategoryCode | HelpCategoryOption): boolean => {
  const value = typeof helpCategory === 'string' ? helpCategory : helpCategory.value;
  return value.toLowerCase() === 'none';
};

export const getHelpCategoryCodes = (helpCategories: readonly HelpCategoryOption[] = []): HelpCategoryCode[] =>
  helpCategories.map((category) => category.value).filter((category) => !isNoneHelpCategory(category));

export const hasRegulatorHelpCategory = (helpCategories: readonly HelpCategoryOption[] = []): boolean =>
  getHelpCategoryCodes(helpCategories).length > 0;

export const normalizeHelpCategoryOptions = (
  selectedOptions: readonly HelpCategoryOption[] | null,
  availableOptions: HelpCategoryOption[]
): HelpCategoryOption[] => {
  const options = selectedOptions ?? [];
  const hasNone = options.some(isNoneHelpCategory);

  if (!hasNone) {
    return [...options];
  }

  return availableOptions.filter(isNoneHelpCategory);
};

export const mapRequestAssistanceFormValuesToAnswers = (values: FormValues): Answers => {
  const helpCategoryCodes = values.helpCategories?.map((category) => category.value) ?? [];
  return {
    helpCategories: helpCategoryCodes.length ? helpCategoryCodes : undefined,
  };
};

export const getRequestAssistanceRecommendation = (answers: Answers): RequestAssistanceRecommendation => {
  const helpCategories = answers.helpCategories ?? [];

  if (helpCategories.length === 0) {
    return null;
  }
  if (helpCategories.some(isNoneHelpCategory)) {
    return 'rrsSupport';
  }
  return 'regulatorAssistance';
};

export const canRequestRegulatorAssistance = (
  userRole: ProfileRoles | string | undefined,
  requestStatus: RequestStatus | string | undefined
): boolean => {
  const isApplicantOrAgent = userRole === ProfileRoles.Applicant || userRole === ProfileRoles.Agent;
  const isDraftRequest = requestStatus === RequestStatus.Draft;

  return isApplicantOrAgent && isDraftRequest;
};

export const canAccessRegulatorAssistance = ({
  permission,
  requestType,
  userRole,
  requestStatus,
}: RegulatorAssistanceParams): boolean => {
  const hasWriteAccess = permission === UserPermissions.ReadWrite || permission === UserPermissions.Owner;
  const isViolationComplaint = requestType === RequestType.ViolationComplaint;
  return hasWriteAccess && !isViolationComplaint && canRequestRegulatorAssistance(userRole, requestStatus);
};

export const getVisibleAssistanceOptions = (
  assistanceOptions: AssistanceOption[],
  showRegulatorAssistance: boolean
): AssistanceOption[] =>
  assistanceOptions.filter((option) => option.id !== 'regulatorAssistance' || showRegulatorAssistance);

export const isRrsSupportRecommendation = (recommendation: RequestAssistanceRecommendation): boolean =>
  recommendation === 'rrsSupport';

export const getRecommendationContent = (recommendation: RequestAssistanceRecommendation) => {
  if (recommendation === 'rrsSupport') {
    return {
      heading: 'Recommended: RRS Support',
      body: 'Based on the selected category, this may be better handled by the RRS Support team. They can help with RRS technical or application issues, such as account access, permissions, saving, editing, submitting, application errors, or file uploads including GDB files.',
    };
  }
  return null;
};
